import DOMPurify from "dompurify";

/**
 * Sanitize rich text HTML (notes) using DOMPurify — allows formatting tags
 * but strips scripts and dangerous attributes.
 */
const sanitizeHtml = (html) => {
  if (!html) return "";
  return DOMPurify.sanitize(html);
};

/**
 * Escape HTML entities in plain-text strings to prevent XSS.
 */
const escapeHtml = (str) => {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

/**
 * Renders a single agenda item's snapshot detail as HTML for printing.
 */
const renderItemDetailHtml = (item) => {
  let detailHtml = "";
  const snap = item.snapshot;

  if (item.source_type === "assessment" && snap) {
    if (snap.term_statuses && snap.term_statuses.length > 0) {
      const pillsHtml = snap.term_statuses
        .map((ts) => {
          const prefix = ts.ended ? "✓ " : ts.isCurrent ? "→ " : "";
          const bg = ts.ended
            ? "#e6f4ea"
            : ts.isCurrent
              ? "#e3f2fd"
              : "#f5f5f5";
          const border = ts.ended
            ? "#34a853"
            : ts.isCurrent
              ? "#1976d2"
              : "#ccc";
          return `<span style="display:inline-block;padding:2px 8px;margin:2px;border-radius:12px;background:${bg};border:1px solid ${border};font-size:12px;">${prefix}${escapeHtml(
            ts.name
          )}</span>`;
        })
        .join("");
      detailHtml += `<div style="margin-top:6px;">${pillsHtml}</div>`;
    }
    if (snap.total_terms > 0) {
      const pct = snap.progress_percentage;
      detailHtml += `<div style="margin-top:6px;"><span style="font-size:12px;color:#555;">${pct}% — ${snap.completed_terms} of ${snap.total_terms} terms completed</span></div>`;
      detailHtml += `<div style="margin-top:4px;height:6px;background:#eee;border-radius:3px;overflow:hidden;"><div style="height:100%;width:${pct}%;background:${
        pct === 100 ? "#34a853" : "#1976d2"
      };border-radius:3px;"></div></div>`;
    }
  } else if (item.source_type === "qualitative_measure" && snap) {
    const statusLabel =
      snap.status === "submitted"
        ? "Submitted"
        : snap.status === "in_progress"
          ? "In progress"
          : "Not started";
    const statusBg =
      snap.status === "submitted"
        ? "#e6f4ea"
        : snap.status === "in_progress"
          ? "#fff3e0"
          : "#f5f5f5";
    const statusBorder =
      snap.status === "submitted"
        ? "#34a853"
        : snap.status === "in_progress"
          ? "#f57c00"
          : "#ccc";
    detailHtml += `<div style="margin-top:6px;"><span style="display:inline-block;padding:2px 8px;border-radius:12px;background:${statusBg};border:1px solid ${statusBorder};font-size:12px;">${statusLabel}</span></div>`;
    if (snap.assignees) {
      detailHtml += `<div style="margin-top:4px;font-size:12px;color:#555;">${escapeHtml(
        snap.assignees
      )}</div>`;
    }
    if (snap.due_term) {
      detailHtml += `<div style="margin-top:4px;font-size:12px;color:#555;">Due: ${escapeHtml(
        snap.due_term
      )}</div>`;
    }
  }

  return detailHtml;
};

/**
 * Sort items by group_order if present. Within each group, items remain in sequence order.
 */
const sortItemsByGroupOrder = (items, groupOrder) => {
  if (!groupOrder?.order?.length) {
    return [...items].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  }

  const mode = groupOrder.mode;
  const orderMap = new Map(groupOrder.order.map((key, idx) => [key, idx]));

  const getGroupKey = (item) => {
    if (mode === "section") {
      return item.section_name || "";
    }
    return item.goal_id || "__ungrouped__";
  };

  return [...items].sort((a, b) => {
    const aGroup = getGroupKey(a);
    const bGroup = getGroupKey(b);
    const aGroupIdx = orderMap.get(aGroup) ?? Infinity;
    const bGroupIdx = orderMap.get(bGroup) ?? Infinity;
    if (aGroupIdx !== bGroupIdx) return aGroupIdx - bGroupIdx;
    // Within same group, maintain sequence order
    return (a.sequence ?? 0) - (b.sequence ?? 0);
  });
};

/**
 * Opens a print window with the agenda content rendered as a clean HTML document.
 * Works for both in-progress (snapshotted at print time) and published agendas.
 *
 * @param {object} agendaData - The agenda object with name, meeting_date, items[], updated_at, updated_by
 * @param {object} options - Optional settings
 * @param {boolean} options.showPublishedFooter - Whether to show "Published by" footer
 */
export const printAgenda = (agendaData, options = {}) => {
  if (!agendaData || !agendaData.items || agendaData.items.length === 0) return;

  const { showPublishedFooter = false } = options;

  const sortedItems = sortItemsByGroupOrder(
    agendaData.items,
    agendaData.group_order
  );

  const mode = agendaData.group_order?.mode;

  const itemsHtml = sortedItems
    .map((item) => {
      const detailHtml = renderItemDetailHtml(item);
      const subtitleText =
        mode === "goalObjective"
          ? [item.goal_description, item.objective_description]
              .filter(Boolean)
              .join(" · ")
          : item.section_name;
      return `<li style="margin-bottom:16px;padding-bottom:12px;border-bottom:1px solid #eee;">
        <strong>${escapeHtml(item.item_name)}</strong>
        <br/><span style="font-size:12px;color:#666;">${escapeHtml(
          subtitleText || ""
        )}</span>
        ${detailHtml}
        ${
          item.notes
            ? `<div style="margin-top:8px;padding:8px;background:#f9f9f9;border-radius:4px;font-size:13px;">${sanitizeHtml(
                item.notes
              )}</div>`
            : ""
        }
      </li>`;
    })
    .join("");

  const footerHtml =
    showPublishedFooter && agendaData.updated_at
      ? `<hr/><p style="color:#666;font-size:11px;">Published ${escapeHtml(
          new Date(agendaData.updated_at).toLocaleDateString("en-CA")
        )} by ${escapeHtml(agendaData.updated_by || "")}</p>`
      : "";

  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(`
      <html><head><title>${escapeHtml(
        agendaData.name || "Meeting Agenda"
      )}</title>
      <style>body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;padding:24px;max-width:800px;margin:0 auto;} li{margin-bottom:16px;list-style-type:decimal;}</style>
      </head><body>
      <h1>${escapeHtml(agendaData.name || "Meeting Agenda")}</h1>
      <p><strong>Meeting Date:</strong> ${escapeHtml(
        agendaData.meeting_date || ""
      )}</p>
      <p><strong>Items:</strong> ${agendaData.items.length}</p>
      <hr/>
      <ol style="padding-left:20px;">${itemsHtml}</ol>
      ${footerHtml}
      </body></html>
    `);
    printWindow.document.close();
    const images = printWindow.document.querySelectorAll("img");
    if (images.length > 0) {
      let loaded = 0;
      const onReady = () => {
        loaded += 1;
        if (loaded >= images.length) {
          printWindow.print();
        }
      };
      images.forEach((img) => {
        if (img.complete) {
          onReady();
        } else {
          img.addEventListener("load", onReady);
          img.addEventListener("error", onReady);
        }
      });
    } else {
      printWindow.print();
    }
  }
};
